import { useCallback, useMemo, useRef } from "react";

type ItemChanges<Item> = Partial<Omit<Item, "id">>;

/** One local change to an item, numbered by when it happened. */
interface JournalEntry<Item> {
  sequence: number;
  changes: ItemChanges<Item>;
}

/** Records local edits so fetches that started before an edit can apply it again. */
export interface LocalChangeJournal<Item extends { id: string }> {
  /** Logs a change made to one item. */
  record: (itemId: string, changes: ItemChanges<Item>) => void;
  /** Marks a fetch as running. Returns the sequence number it started at. */
  beginFetch: () => number;
  /** Marks the fetch that started at `startSequence` as done and drops changes no fetch needs. */
  endFetch: (startSequence: number) => void;
  /** Applies the changes made after `startSequence` to the fetched items, oldest first. */
  applyChangesSince: (items: Item[], startSequence: number) => Item[];
}

/**
 * A journal of local edits, numbered in the order they happened, plus the fetches still running.
 * A fetch that started before an edit lands with an older copy of the item; replaying the newer
 * edits over it keeps that copy from undoing them. Edits no running fetch can need are dropped,
 * so the journal stays small.
 *
 * @returns stable functions to record edits, track fetches, and replay edits over a fetch result.
 */
export default function useLocalChangeJournal<
  Item extends { id: string },
>(): LocalChangeJournal<Item> {
  const changeSequence = useRef(0);
  const changesByItem = useRef(new Map<string, JournalEntry<Item>[]>());
  // The sequence number each running fetch started at.
  const runningFetchSequences = useRef<number[]>([]);

  const record = useCallback((itemId: string, changes: ItemChanges<Item>) => {
    const itemChanges = changesByItem.current.get(itemId) ?? [];
    itemChanges.push({ sequence: ++changeSequence.current, changes });
    changesByItem.current.set(itemId, itemChanges);
  }, []);

  const beginFetch = useCallback(() => {
    const startSequence = changeSequence.current;
    runningFetchSequences.current.push(startSequence);
    return startSequence;
  }, []);

  const endFetch = useCallback((startSequence: number) => {
    const running = runningFetchSequences.current;
    const index = running.indexOf(startSequence);
    if (index === -1) return;
    // Fetches that started at the same sequence need the same changes, so removing any one of
    // their entries is the same as removing this fetch's.
    running.splice(index, 1);

    const oldestRunningFetch = running.length ? Math.min(...running) : changeSequence.current;
    dropChangesUpTo(changesByItem.current, oldestRunningFetch);
  }, []);

  const applyChangesSince = useCallback(
    (items: Item[], startSequence: number) =>
      items.map((item) =>
        (changesByItem.current.get(item.id) ?? [])
          .filter(({ sequence }) => sequence > startSequence)
          .reduce<Item>((updated, { changes }) => ({ ...updated, ...changes }), item),
      ),
    [],
  );

  return useMemo(
    () => ({ record, beginFetch, endFetch, applyChangesSince }),
    [record, beginFetch, endFetch, applyChangesSince],
  );
}

/**
 * Removes the changes at or before `sequence`, which no running fetch replays, and the items
 * left with none.
 *
 * @param changesByItem - each item's changes, oldest first. Updated in place.
 * @param sequence - the start sequence of the oldest running fetch, or the latest sequence when
 * no fetch is running.
 */
function dropChangesUpTo<Item>(
  changesByItem: Map<string, JournalEntry<Item>[]>,
  sequence: number,
): void {
  for (const [itemId, itemChanges] of changesByItem) {
    const remaining = itemChanges.filter((entry) => entry.sequence > sequence);
    if (remaining.length) changesByItem.set(itemId, remaining);
    else changesByItem.delete(itemId);
  }
}
